import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { MapPin, Mail, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { submitContact } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const WHATSAPP_NUMBER = '35795989569';

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContact({
        ...formData,
        language,
      });
      toast.success(t('contact.success'), { description: t('contact.successDesc') });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Error submitting form');
    } finally {
      setSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const message = language === 'ru' 
      ? 'Здравствуйте! Меня интересуют виллы на первой линии от моря в Хорватии.'
      : 'Hello! I am interested in first line villas in Croatia.';
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <>
      <Helmet>
        <title>{`${t('contact.title')} — Villa.ooo`}</title>
        <meta name="description" content={String(t('contact.description'))} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://villa.ooo/contact" />
      </Helmet>

      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] flex items-end" data-testid="contact-hero">
        <div className="absolute inset-0">
          <img
            src="/contact-bg.jpg"
            alt="Contact Villa.ooo"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-12 md:pb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="label-style text-gold mb-4">{t('contact.label')}</p>
            <h1 className="heading-hero font-heading text-white">{t('contact.title')}</h1>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-text-secondary text-lg leading-relaxed mb-12">
                {t('contact.description')}
              </p>

              <div className="space-y-8">
                {/* WhatsApp - Primary */}
                <button
                  onClick={openWhatsApp}
                  className="flex items-start gap-4 w-full text-left group"
                  data-testid="contact-whatsapp-btn"
                >
                  <div className="w-12 h-12 bg-[#25D366]/10 flex items-center justify-center group-hover:bg-[#25D366] transition-colors">
                    <MessageCircle className="w-5 h-5 text-[#25D366] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h4 className="font-body font-medium text-night mb-1">WhatsApp</h4>
                    <p className="text-text-secondary group-hover:text-[#25D366] transition-colors">
                      {language === 'en' ? 'Message us now' : 'Schreiben Sie uns'}
                    </p>
                  </div>
                </button>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <h4 className="font-body font-medium text-night mb-1">Email</h4>
                    <a href="mailto:bm@villa.ooo" className="text-text-secondary hover:text-gold transition-colors">
                      bm@villa.ooo
                    </a>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <h4 className="font-body font-medium text-night mb-1">
                      {language === 'en' ? 'Region' : 'Region'}
                    </h4>
                    <p className="text-text-secondary">
                      Dalmatia, Croatia
                    </p>
                    <p className="text-text-secondary text-sm">
                      Trogir • Split • Primosten • Omis
                    </p>
                  </div>
                </div>
              </div>

              {/* Big WhatsApp Button */}
              <button
                onClick={openWhatsApp}
                className="mt-12 w-full bg-[#25D366] text-white py-4 text-sm tracking-widest uppercase flex items-center justify-center gap-3 hover:bg-[#128C7E] transition-colors"
                data-testid="contact-whatsapp-cta"
              >
                <MessageCircle className="w-5 h-5" />
                {language === 'ru' ? 'Написать в WhatsApp' : 'Message on WhatsApp'}
              </button>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h3 className="font-heading text-2xl text-night mb-8">
                {language === 'ru' ? 'Или оставьте заявку' : 'Or leave a request'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                <div>
                  <label className="label-style block mb-3">{t('contact.name')}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-minimal w-full"
                    required
                    data-testid="contact-page-name"
                  />
                </div>

                <div>
                  <label className="label-style block mb-3">{t('contact.email')}</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-minimal w-full"
                    required
                    data-testid="contact-page-email"
                  />
                </div>

                <div>
                  <label className="label-style block mb-3">{t('contact.phone')}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-minimal w-full"
                    data-testid="contact-page-phone"
                  />
                </div>

                <div>
                  <label className="label-style block mb-3">{t('contact.message')}</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="input-minimal w-full min-h-[150px] resize-none"
                    required
                    data-testid="contact-page-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary px-10 py-4 text-sm tracking-widest uppercase"
                  data-testid="contact-page-submit"
                >
                  {submitting ? '...' : t('contact.send')}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Floating WhatsApp Button */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:bg-[#128C7E] transition-colors hover:scale-110"
        data-testid="floating-whatsapp-btn"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </button>
    </>
  );
};

export default ContactPage;
